import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../Store/store.ts';
import {
  CarInformationSelector,
  CurrentStepDataSelector,
  CurrentStepSelector,
  HasLoadedFromCookieSelector,
  InitialDataSelector,
  ShowMoreInfoSelector,
  ShowResetButtonsSelector,
  StepDataListSelector,
  StepHasProductSearchSelector,
  StepHasSelectedOptionSelector,
  TranslationsSelector,
} from '../../Selectors/FitAssistantSelectors';
import { fetchListOptions } from '../../Thunks/FetchListOptions.ts';
import { getOptions, getProducts } from '../../Thunks/Queries.ts';
import { resetFitAssistant } from '../../Thunks/Reset.ts';
import { CarInformation, StepDataData } from '../../Types/FitAssistantData.ts';
import { addCarInformation, selectOption, setFooterIsFixed } from '../../Slices/FitAssistantSlice.ts';
import { FooterHelper } from '../../Helpers/MoreInfoHelper.ts';
import { ProductSearchType } from '../../Enums/ProductSearchType.ts';
import { trackChooseModel, trackChooseVariation1, trackChooseYear, trackOK } from '../../Analytics/Adobe.ts';
import Buttons from '../Buttons/Buttons.tsx';
import FitAssistantStep from '../../Enums/FitAssistantStep.ts';
import PrimaryButton from '../Buttons/PrimaryButton.tsx';
import SecondaryButton from '../Buttons/SecondaryButton.tsx';
import SelectListItemForProductSearch from './SelectListItemForProductSearch.tsx';
import SelectListItem from './SelectListItem.tsx';

import './SelectList.less';

const SelectList = () => {
  const [query, setQuery] = useState('');
  const isSelectingRef = useRef(false);

  const dispatch = useAppDispatch();
  const carInformation = useAppSelector(CarInformationSelector);
  const currentStep = useAppSelector(CurrentStepSelector);
  const currentStepData = useAppSelector(CurrentStepDataSelector);
  const hasLoadedFromCookie = useAppSelector(HasLoadedFromCookieSelector);
  const initialData = useAppSelector(InitialDataSelector);
  const showMoreInfo = useAppSelector(ShowMoreInfoSelector);
  const showResetbutton = useAppSelector(ShowResetButtonsSelector);
  const stepDataList = useAppSelector(StepDataListSelector);
  const stepHasProductSearch = useAppSelector(StepHasProductSearchSelector);
  const stepHasSelectedOption = useAppSelector(StepHasSelectedOptionSelector);
  const translations = useAppSelector(TranslationsSelector);

  useEffect(() => {
    if (!isSelectingRef.current) {
      setQuery('');
    }
    isSelectingRef.current = false;

    if (!hasLoadedFromCookie || stepDataList.length > 0) return;
    dispatch(getOptions());
  }, [stepDataList, hasLoadedFromCookie]);

  useEffect(() => {
    let result = FooterHelper.FooterShouldBeFixed('.select-list ');
    dispatch(setFooterIsFixed(result));
  }, [stepDataList]);

  const getCarInformation = (option: StepDataData): CarInformation[] => {
    const configItem = stepDataList?.find((x) => x.parameters === currentStep)?.configItems;
    const year = {
      parameterName: currentStep,
      configurationItem: configItem,
      text: option.Text,
      value: option.Value,
    };
    let updCarInformation = [...carInformation.filter((x) => x.parameterName != currentStep), year] as CarInformation[];
    return updCarInformation;
  };

  const normalizeString = (inputString: string) => {
    return inputString
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  const generateOptionItems = () => {
    return stepDataList
      .find((x) => x.parameters === currentStep)
      ?.data?.filter((x) => normalizeString(x.Text).includes(normalizeString(query)))
      .map((item, index) => {
        return stepHasProductSearch ? (
          <SelectListItemForProductSearch
            item={item}
            key={index}
            isSelected={item.Selected}
            onSelect={() => onSelect(item)}
          />
        ) : (
          <SelectListItem item={item} onClick={onClickSelectListItem} key={index} />
        );
      });
  };

  const onSelect = (item: StepDataData): void => {
    isSelectingRef.current = true;
    let selectedOption = currentStepData?.data.find((x) => x.Value === item.Value);
    let index = currentStepData?.data.indexOf(selectedOption);
    if (!selectedOption?.Selected) {
      // Only fire a "choose" event for steps that have one. Any step can be the
      // product-search (confirm) step depending on category, e.g. Model for trunk
      // bike racks - firing carvariation1 there sent an empty value (ADO #116).
      if (currentStep === FitAssistantStep.Year) {
        trackChooseYear(initialData.FitAssistantVariation, initialData.IsYearFirst, getCarInformation(item));
      } else if (currentStep === FitAssistantStep.Model) {
        trackChooseModel(initialData.FitAssistantVariation, initialData.IsYearFirst, getCarInformation(item));
      } else if (currentStep === FitAssistantStep.VanSize) {
        trackChooseVariation1(initialData.FitAssistantVariation, initialData.IsYearFirst, getCarInformation(item));
      }
    }
    dispatch(selectOption(index));
  };

  const onClickSelectListItem = (item: StepDataData): void => {
    if (currentStep === FitAssistantStep.Year) {
      trackChooseYear(initialData.FitAssistantVariation, initialData.IsYearFirst, getCarInformation(item));
    } else if (currentStep === FitAssistantStep.Model) {
      trackChooseModel(initialData.FitAssistantVariation, initialData.IsYearFirst, getCarInformation(item));
    }
    dispatch(
      fetchListOptions({
        text: item.Text,
        value: item.Value,
        parameter: stepDataList[stepDataList.length - 1].parameters,
      })
    );
  };

  const onClickPrimary = () => {
    let item = stepDataList.find((x) => x.parameters === currentStep)?.data?.find((x) => x.Selected);
    if (item) dispatch(addCarInformation(getCarInformation(item)));

    dispatch(getProducts(ProductSearchType.SelectList));
    trackOK(initialData.FitAssistantVariation, currentStep, initialData.IsYearFirst, carInformation);
  };

  const onClickSecondary = () => {
    dispatch(resetFitAssistant(false));
  };

  return (
    <>
      {!showMoreInfo && (
        <div className="select-list d-flex flex-column justify-content-center">
          <div className={`search-bar ${currentStep === FitAssistantStep.Year ? 'd-none' : ''}`}>
            <input
              type="text"
              placeholder={translations.search}
              onChange={(e) => setQuery(e.target.value)}
              value={query}
            />
            <i className="le-icon-search"></i>
          </div>
          <div className="list-wrapper">{generateOptionItems()}</div>
        </div>
      )}

      <Buttons visible={stepHasProductSearch && stepHasSelectedOption && !showMoreInfo}>
        <PrimaryButton visible text={translations.confirm} onClick={onClickPrimary} />
        <SecondaryButton visible={showResetbutton} text={translations.reset} onClick={onClickSecondary} />
      </Buttons>
    </>
  );
};

export default SelectList;
